import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Users, Globe, CreditCard, Mail, LogOut, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SAR = (n) => (n || 0).toLocaleString("en-US");

export default function Admin() {
    const { user, logout, authAxios } = useAuth();
    const [stats, setStats] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [s, c, o] = await Promise.all([
                    authAxios.get("/admin/stats"),
                    authAxios.get("/admin/customers"),
                    authAxios.get("/admin/orders"),
                ]);
                setStats(s.data);
                setCustomers(c.data.customers);
                setOrders(o.data.orders);
            } catch {
                toast.error("Could not load admin data");
            } finally {
                setLoading(false);
            }
        })();
    }, [authAxios]);

    const searchCustomers = async (e) => {
        e.preventDefault();
        try {
            const { data } = await authAxios.get(`/admin/customers?q=${encodeURIComponent(q)}`);
            setCustomers(data.customers);
        } catch {
            toast.error("Search failed");
        }
    };

    if (loading || !stats) {
        return (
            <main className="min-h-screen bg-limestone-100 pt-32 flex items-center justify-center" data-testid="admin-loading">
                <Loader2 className="h-10 w-10 animate-spin text-madder-500" />
            </main>
        );
    }

    return (
        <main className="min-h-screen bg-limestone-100 pt-28 pb-24" data-testid="admin-portal">
            <div className="asir-stripe h-[6px]" aria-hidden="true" />

            <div className="mx-auto max-w-[1320px] px-5 pt-12 lg:px-8">
                {/* Header */}
                <div className="flex flex-wrap items-start justify-between gap-4 border-b-2 border-ink-800 pb-10">
                    <div>
                        <p className="eyebrow">NourNet · Admin portal</p>
                        <h1 className="section-headline mt-3" style={{ fontSize: "clamp(36px, 4.4vw, 56px)" }}>
                            Control room
                        </h1>
                        <p className="mt-3 text-[17px] font-medium text-ink-600">
                            Signed in as <span className="font-mono font-bold text-ink-800 num-ltr">{user.email}</span>
                        </p>
                    </div>
                    <button type="button" onClick={logout} data-testid="admin-logout" className="inline-flex h-[48px] items-center gap-2 rounded-btn border-2 border-ink-800 bg-white px-4 text-[15px] font-bold text-ink-800 hover:bg-ink-800 hover:text-white">
                        <LogOut className="h-4 w-4" /> Sign out
                    </button>
                </div>

                {/* KPI grid */}
                <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-kpis">
                    {[
                        { label: "Customers", value: stats.users.customers, icon: Users, bg: "bg-madder-500", fg: "text-white" },
                        { label: "Active Domains", value: stats.domains.active, total: stats.domains.total, icon: Globe, bg: "bg-redsea-500", fg: "text-white" },
                        { label: "Paid Orders", value: stats.orders.paid, icon: CreditCard, bg: "bg-souq-400", fg: "text-ink-900" },
                        { label: "Gross Revenue", value: `${SAR(stats.orders.gross_sar)}`, suffix: " SAR", icon: Mail, bg: "bg-ink-800", fg: "text-white", small: true },
                    ].map((s) => (
                        <div key={s.label} className={`rounded-card p-6 ${s.bg} ${s.fg}`} data-testid={`kpi-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                            <div className="flex items-center justify-between">
                                <s.icon className="h-5 w-5" />
                                <p className="text-[11px] font-bold uppercase tracking-[0.18em] opacity-70">{s.label}</p>
                            </div>
                            <p className={`mt-5 font-display font-extrabold leading-none tabular-nums num-ltr ${s.small ? "text-[28px]" : "text-[44px]"}`}>
                                {s.value}{s.suffix || ""}
                            </p>
                            {s.total !== undefined && <p className="mt-2 text-[12px] font-bold opacity-70 num-ltr">of {s.total} total</p>}
                        </div>
                    ))}
                </div>

                {/* Customers table */}
                <section className="mt-14" data-testid="admin-customers">
                    <div className="flex flex-wrap items-center justify-between mb-5 gap-4">
                        <h2 className="font-display text-[28px] font-bold tracking-tight text-ink-800">Customers</h2>
                        <form onSubmit={searchCustomers} className="flex items-center gap-2">
                            <div className="flex h-[44px] items-center gap-2 rounded-btn border-2 border-ink-300 bg-white px-3 focus-within:border-ink-800">
                                <Search className="h-4 w-4 text-ink-500" />
                                <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…" data-testid="admin-customer-search" className="h-full w-[260px] bg-transparent text-[14px] font-medium text-ink-800 placeholder:text-ink-400 focus:outline-none" />
                            </div>
                            <button type="submit" className="inline-flex h-[44px] items-center rounded-btn bg-ink-800 px-4 text-[14px] font-bold text-white hover:bg-madder-500">Go</button>
                        </form>
                    </div>
                    <div className="overflow-hidden rounded-card border-2 border-ink-200 bg-white">
                        <div className="hidden md:grid grid-cols-12 gap-4 border-b-2 border-ink-200 bg-mudbrick-100 px-6 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-ink-700">
                            <div className="col-span-4">Customer</div>
                            <div className="col-span-3">Email</div>
                            <div className="col-span-2">Role</div>
                            <div className="col-span-2">Domains</div>
                            <div className="col-span-1 text-end">Auth</div>
                        </div>
                        {customers.map((c) => (
                            <div key={c.user_id} className="grid grid-cols-1 md:grid-cols-12 gap-4 border-b border-ink-100 px-6 py-4 last:border-b-0 hover:bg-mudbrick-100/50" data-testid={`customer-row-${c.user_id}`}>
                                <div className="col-span-4 flex items-center gap-3">
                                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-madder-500 text-white font-bold text-[14px]">
                                        {(c.name || c.email || "?").slice(0, 1).toUpperCase()}
                                    </span>
                                    <p className="text-[16px] font-bold text-ink-800">{c.name || "—"}</p>
                                </div>
                                <div className="col-span-3 font-mono text-[14px] font-semibold text-ink-700 num-ltr truncate">{c.email}</div>
                                <div className="col-span-2">
                                    <span className={`inline-flex rounded-[4px] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${c.role === "admin" ? "bg-ink-800 text-white" : "bg-redsea-500 text-white"}`}>{c.role}</span>
                                </div>
                                <div className="col-span-2 font-mono text-[14px] font-bold text-ink-800 tabular-nums num-ltr">{c.domains_count}</div>
                                <div className="col-span-1 text-end">
                                    <span className="inline-flex rounded-[4px] bg-mudbrick-200 px-2 py-0.5 font-mono text-[11px] font-bold uppercase tracking-wider text-ink-700">{c.auth_provider || "—"}</span>
                                </div>
                            </div>
                        ))}
                        {customers.length === 0 && <p className="p-6 text-[15px] text-ink-500">No customers match.</p>}
                    </div>
                </section>

                {/* Orders table */}
                <section className="mt-14" data-testid="admin-orders">
                    <h2 className="mb-5 font-display text-[28px] font-bold tracking-tight text-ink-800">Recent orders</h2>
                    <div className="overflow-hidden rounded-card border-2 border-ink-200 bg-white">
                        {orders.length === 0 && <p className="p-8 text-center text-[15px] text-ink-500">No paid orders yet. Try checkout as <Link to="/login" className="text-madder-500 font-bold underline">demo customer</Link>.</p>}
                        {orders.map((o) => (
                            <div key={o.txn_id} className="grid grid-cols-1 md:grid-cols-12 gap-4 border-b border-ink-100 px-6 py-4 last:border-b-0" data-testid={`order-row-${o.txn_id}`}>
                                <div className="col-span-4">
                                    <p className="font-mono text-[14px] font-bold text-ink-800 num-ltr">{o.txn_id.slice(0, 16)}…</p>
                                    <p className="mt-0.5 text-[12px] font-semibold text-ink-500 num-ltr">{(o.created_at || "").slice(0, 19).replace("T", " ")}</p>
                                </div>
                                <div className="col-span-4 font-mono text-[13px] text-ink-700 num-ltr">{o.user_email}</div>
                                <div className="col-span-2 font-mono text-[14px] font-bold text-ink-800 tabular-nums num-ltr">{SAR(o.amount_sar)} SAR</div>
                                <div className="col-span-2 text-end">
                                    <span className={`inline-flex rounded-[4px] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${o.status === "paid" ? "bg-redsea-500 text-white" : o.status === "pending" ? "bg-mudbrick-400 text-white" : "bg-ink-500 text-white"}`}>{o.status}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </main>
    );
}
